import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import App from './App.tsx'
import './index.css'
import { initSentry } from './lib/sentry'
import { monitoring } from './lib/monitoring'

// Global error handler to prevent blank screens
const showFallbackUI = (error: Error | string) => {
  const rootEl = document.getElementById("root");
  if (rootEl) {
    rootEl.innerHTML = `
      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:system-ui,sans-serif;padding:20px;text-align:center;background:#f9fafb;">
        <h1 style="color:#1f2937;font-size:1.5rem;margin-bottom:1rem;">Une erreur est survenue</h1>
        <p style="color:#6b7280;margin-bottom:1.5rem;">L'application n'a pas pu démarrer correctement.</p>
        <button onclick="window.location.reload()" style="background:#3b82f6;color:white;padding:10px 20px;border:none;border-radius:6px;cursor:pointer;font-size:1rem;">
          Recharger la page
        </button>
        <p style="color:#9ca3af;font-size:0.75rem;margin-top:2rem;max-width:400px;">
          ${typeof error === 'string' ? error : error.message || 'Erreur inconnue'}
        </p>
      </div>
    `;
  }
};

// Capture unhandled errors before React mounts
window.addEventListener('error', (event) => {
  console.error('[Global Error]', event.error);
  // Only show fallback if React hasn't mounted yet
  const rootEl = document.getElementById("root");
  if (rootEl && rootEl.children.length === 0) {
    showFallbackUI(event.error || 'Erreur de chargement');
  }
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('[Unhandled Promise Rejection]', event.reason);
});

// Initialize error tracking
try {
  initSentry();
  monitoring.setupGlobalErrorHandlers();
  monitoring.trackWebVitals();
} catch (e) {
  console.warn('[Monitoring] Failed to initialize:', e);
}

// Force unregister all existing service workers to purge corrupted cache
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      registration.unregister();
      console.log('[SW] Unregistered:', registration.scope);
    });
  }).catch(console.error);
}

// Bootstrap React with error handling
try {
  const rootEl = document.getElementById("root");
  if (!rootEl) {
    throw new Error("Root element not found");
  }
  
  createRoot(rootEl).render(
    <HelmetProvider>
      <App />
    </HelmetProvider>
  );
} catch (error) {
  console.error('[Bootstrap Error]', error);
  showFallbackUI(error instanceof Error ? error : new Error(String(error)));
}
